import {sortBy} from 'lodash';

import {stringifyQueryString} from '../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSelectionParameters,
} from './inboxesParameters';
import {
  computeIsAllExceptSelection,
  InboxesAllExceptSelection,
  inboxesParametersSelectAllKeyword,
  InboxesSelection,
  InboxesSelectionIdType,
} from './inboxesParametersHelpers';

/** Build the route path from inboxes parameters. */
export function buildParametersPath(parameters: InboxesParameters) {
  // Build the selected IDs suffix, if needed.
  const selected = buildInboxesParametersSelectionSuffix(parameters.selection);

  // Message parameters.
  if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    return `/inboxes/teammates/${parameters.teammateId}/${parameters.folder}${selected}`;
  }

  // Build the search suffix, if needed.
  const search = buildParametersPathSearchSuffix(parameters);
  const filters = sortBy(parameters.filters).join(',');

  // Build the query string, if needed.
  const {activity} = parameters;
  const activityQuery = stringifyQueryString({
    around: activity && 'id' in activity ? activity.id : undefined,
    sort: activity && 'date' in activity && activity.date === 0 ? 'first' : undefined,
    date: activity && 'date' in activity && activity.date > 0 ? activity.date : undefined,
  });

  // Build the final path.
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
      return `/inboxes/teams/folders/${parameters.inboxId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return `/inboxes/teammates/folders/${parameters.inboxId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.VIEW:
      return `/inboxes/teams/views/${parameters.viewId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return `/inboxes/teammates/views/${parameters.viewId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.TEAMMATE_LENS:
      return `/inboxes/teammates/lenses/${parameters.lensId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.TEAM_LENS:
      return `/inboxes/teams/lenses/${parameters.lensId}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.USER: {
      const {teammateId, folder} = parameters;
      return `/inboxes/teammates/${teammateId}/${folder}/${filters}${search}${selected}${activityQuery}`;
    }

    case InboxesParametersTypesEnum.TEAMMATE: {
      const {teammateId, folder} = parameters;
      return `/inboxes/teammates/${teammateId}/${folder}/${filters}${search}${selected}${activityQuery}`;
    }

    case InboxesParametersTypesEnum.TEAM:
      return `/inboxes/teams/${parameters.teamId}/${parameters.folder}/${filters}${search}${selected}${activityQuery}`;

    case InboxesParametersTypesEnum.GLOBAL:
      return `/inboxes/global/${parameters.folder}/${filters}${search}${selected}${activityQuery}`;

    default:
      throw new UnreachableError(parameters);
  }
}

function buildParametersPathSearchSuffix({search}: InboxesConversationsParameters) {
  // If no search parameters were specified, nothing to do.
  if (!search) {
    return '';
  }

  // Otherwise, build the suffix.
  const {location, query, fromSelection} = search;
  const fromSelectionStr = buildParametersPathSearchSuffixSelection(fromSelection);
  const sanitizedQuery = query.replace(/\s*[\r\n\f]+\s*/g, ' ');
  return `/${fromSelectionStr}/search/${location}/${encodeURIComponent(sanitizedQuery)}`;
}

function buildParametersPathSearchSuffixSelection(fromSelection?: InboxesSelectionParameters): string {
  if (!fromSelection) {
    return '0';
  }

  if (computeIsAllExceptSelection(fromSelection)) {
    return buildParametersPathSelectionAllExceptSection(fromSelection);
  }

  if (fromSelection.selectedIds.length === 0) {
    return '0';
  }

  return `${fromSelection.selectedIds.join(',')}`;
}

export function buildInboxesParametersSelectionSuffix(
  selection: InboxesSelection<InboxesSelectionIdType>,
): string {
  if (computeIsAllExceptSelection(selection)) {
    return `/${buildParametersPathSelectionAllExceptSection(selection)}`;
  }

  // If no selected IDs were provided, nothing to do.
  if (selection.selectedIds.length === 0) {
    return '';
  }

  // Otherwise, build the suffix.
  return `/${selection.selectedIds.join(',')}`;
}

function buildParametersPathSelectionAllExceptSection(
  selection: InboxesAllExceptSelection<InboxesSelectionIdType>,
): string {
  const deselectTokens = selection.deselectedIds.map((id: InboxesSelectionIdType) => `~${id}`);
  const tokens = [inboxesParametersSelectAllKeyword, ...deselectTokens];
  return `${tokens.join(',')}`;
}
